import type { Preset } from './types';

export type BrowserKind = 'chrome' | 'safari' | 'firefox' | 'samsung' | 'other';
export type InAppKind = 'kakao' | 'naver' | 'meta' | 'line' | 'none';
export type OsKind = 'android' | 'ios' | 'other';

export interface EnvProbe {
  readonly os: OsKind;
  readonly osv: number;
  readonly br: BrowserKind;
  readonly iab: InAppKind;
  readonly scw: number;
  readonly sch: number;
  readonly ptr: string;
  readonly tp: number;
  readonly dsk: boolean;
  readonly tz: string;
  readonly ref: string;
}

const IN_APP: readonly (readonly [InAppKind, RegExp])[] = [
  ['kakao', /KAKAOTALK/i],
  ['naver', /NAVER\(inapp/i],
  ['meta', /FBAN|FBAV|Instagram/i],
  ['line', /\bLine\//i],
];

export function classifyBrowser(ua: string): BrowserKind {
  if (/SamsungBrowser/i.test(ua)) return 'samsung';
  if (/FxiOS|Firefox/i.test(ua)) return 'firefox';
  if (/CriOS|Chrome/i.test(ua)) return 'chrome';
  if (/Safari/i.test(ua)) return 'safari';
  return 'other';
}

export function classifyInApp(ua: string): InAppKind {
  for (const [k, re] of IN_APP) if (re.test(ua)) return k;
  return 'none';
}

/** 인앱 브라우저는 UA 에서 OS 를 지우기도 한다. 실패는 -1 로 남기고 버리지 않는다. */
export function osMajor(ua: string): number {
  const a = /Android\s+(\d+)/i.exec(ua);
  if (a !== null) return Number(a[1]);
  const i = /OS\s+(\d+)[._]/i.exec(ua);
  if (i !== null) return Number(i[1]);
  return -1;
}

/** 참가자 코드 파생. URL 오버라이드가 아니라 층화 변수다(SPEC §15.2). */
export function presetOf(participant: string | null): Preset {
  // 참가자 코드가 없으면 'B' — PRESET_DISTRACTORS.B 가 RULES 확정값(8)과 같다.
  // 'A'(6) 로 두면 링크로 들어온 실사용자 전원이 규격 밖 오답 수로 돈다.
  if (participant === null) return 'B';
  const n = Number.parseInt(participant, 10);
  const idx = Number.isFinite(n) ? ((n % 3) + 3) % 3 : 1;
  return (['A', 'B', 'C'] as const)[idx] as Preset;
}

export function probeEnv(vw: number, vh: number): EnvProbe {
  const ua = navigator.userAgent;
  const tp = navigator.maxTouchPoints;
  // 구형 WebView 에 matchMedia 가 없으면 부팅이 session_start 이전에 죽어 참가자가 표본에서 사라진다 —
  // 계측 실패가 게임 실패가 되면 안 된다. 실패 시 coarse=false/fine=false 로 두고 maxTouchPoints 가 교차 근거가 된다.
  const media = (q: string): boolean => {
    try {
      return typeof window.matchMedia === 'function' && window.matchMedia(q).matches;
    } catch {
      return false;
    }
  };
  const coarse = media('(pointer: coarse)');
  const fine = media('(pointer: fine)');
  let ref = '';
  try {
    ref = document.referrer === '' ? '' : new URL(document.referrer).host;
  } catch {
    ref = '';
  }
  let tz = '';
  try {
    tz = new Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    tz = '';
  }
  return {
    os: /Android/i.test(ua) ? 'android' : /iPhone|iPad|iPod/i.test(ua) ? 'ios' : 'other',
    osv: osMajor(ua),
    br: classifyBrowser(ua),
    iab: classifyInApp(ua),
    scw: window.screen.width,
    sch: window.screen.height,
    ptr: coarse ? 'coarse' : fine ? 'fine' : 'none',
    tp,
    // 폰을 가로로 든 세션이 통째로 빠지지 않도록 min(vw,vh) 을 쓴다
    dsk: !coarse || (Math.min(vw, vh) >= 768 && tp === 0),
    tz,
    ref,
  };
}
